/**
 * Collection tabs from babyspotlight: show one tab's products without a reload.
 * Triggers are rendered inside each tab item, then moved into the shared tablist.
 * Header arrows control the active resource-list carousel.
 */
class TabSwitchProducts extends HTMLElement {
  /** @type {HTMLElement | null} */
  #tablist = null;

  /** @type {HTMLElement[]} */
  #items = [];

  /** @type {HTMLElement[]} */
  #triggers = [];

  /** @type {number} */
  #activeIndex = 0;

  /** @type {HTMLElement | null} */
  #section = null;

  /** @type {HTMLButtonElement | null} */
  #prevButton = null;

  /** @type {HTMLButtonElement | null} */
  #nextButton = null;

  connectedCallback() {
    this.#tablist = this.querySelector('[data-tablist]');
    if (!this.#tablist) return;

    this.#section = this.closest('.section-tabbed-products');
    this.#prevButton = this.#section?.querySelector('[data-tabbed-products-prev]') || null;
    this.#nextButton = this.#section?.querySelector('[data-tabbed-products-next]') || null;

    this.#sync();
    if (this.#items.length === 0) return;

    this.#activate(0, false);

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('slideshow:select', this.#handleSlideshowSelect);
    this.#prevButton?.addEventListener('click', this.#handlePrev);
    this.#nextButton?.addEventListener('click', this.#handleNext);

    if (window.Shopify?.designMode) {
      this.#section?.addEventListener('shopify:block:select', this.#handleBlockSelect);
    }

    this.classList.add('is-ready');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener('slideshow:select', this.#handleSlideshowSelect);
    this.#section?.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    this.#prevButton?.removeEventListener('click', this.#handlePrev);
    this.#nextButton?.removeEventListener('click', this.#handleNext);
  }

  #sync() {
    this.#items = Array.from(this.querySelectorAll('[data-tab-item]'));
    if (!this.#tablist) return;

    this.#items.forEach((item) => {
      const trigger = item.querySelector('[data-tab-trigger]');
      if (trigger instanceof HTMLElement) {
        this.#tablist.append(trigger);
      }
    });

    this.#triggers = Array.from(this.#tablist.querySelectorAll('[data-tab-trigger]'));
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const trigger = target.closest('[data-tab-trigger]');
    if (!(trigger instanceof HTMLElement) || !this.#tablist?.contains(trigger)) return;

    const index = this.#triggers.indexOf(trigger);
    if (index < 0 || index === this.#activeIndex) return;

    this.#activate(index, true);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!(target instanceof HTMLElement) || !target.closest('[data-tab-trigger]')) return;
    if (!this.#tablist?.contains(target)) return;

    const last = this.#triggers.length - 1;
    let next = this.#activeIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = this.#activeIndex === last ? 0 : this.#activeIndex + 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = this.#activeIndex === 0 ? last : this.#activeIndex - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(next, true);
    this.#triggers[next]?.focus();
  };

  /**
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    this.#sync();

    const target = /** @type {HTMLElement} */ (event.target);
    const item = target.closest?.('[data-tab-item]');
    const trigger = target.closest?.('[data-tab-trigger]');
    const editorTab = target.closest?.('[data-tab-id]');

    let index = -1;
    if (item instanceof HTMLElement) {
      index = this.#items.indexOf(item);
    } else if (trigger instanceof HTMLElement) {
      index = this.#triggers.indexOf(trigger);
    } else if (editorTab instanceof HTMLElement && editorTab.dataset.tabId) {
      index = this.#items.findIndex((tabItem) => tabItem.dataset.tabId === editorTab.dataset.tabId);
    }

    if (index >= 0) this.#activate(index, true);
  };

  /**
   * @param {Event} event
   */
  #handleSlideshowSelect = (event) => {
    const panel = this.#activePanel;
    if (panel instanceof HTMLElement && panel.contains(event.target)) {
      this.#syncArrows();
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #handlePrev = (event) => {
    event.preventDefault();
    this.#activeSlideshow?.previous?.(event);
  };

  /**
   * @param {MouseEvent} event
   */
  #handleNext = (event) => {
    event.preventDefault();
    this.#activeSlideshow?.next?.(event);
  };

  /**
   * @param {number} index
   * @param {boolean} animate
   */
  #activate(index, animate) {
    if (this.#items.length === 0) return;

    this.#activeIndex = Math.max(0, Math.min(index, this.#items.length - 1));

    this.#items.forEach((item, itemIndex) => {
      const isActive = itemIndex === this.#activeIndex;
      const panel = item.querySelector('[data-tab-panel]');
      const trigger = this.#triggers[itemIndex];

      item.classList.toggle('is-active', isActive);

      if (panel instanceof HTMLElement) {
        panel.toggleAttribute('hidden', !isActive);
        panel.classList.toggle('is-active', isActive);
        if (animate && isActive) {
          panel.classList.remove('is-animating');
          void panel.offsetWidth;
          panel.classList.add('is-animating');
        }
      }

      if (trigger instanceof HTMLElement) {
        trigger.setAttribute('aria-selected', String(isActive));
        trigger.tabIndex = isActive ? 0 : -1;
        trigger.classList.toggle('is-active', isActive);

        if (isActive && animate) {
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          trigger.scrollIntoView({
            inline: 'center',
            block: 'nearest',
            behavior: reduceMotion ? 'auto' : 'smooth',
          });
        }
      }

      if (isActive && panel instanceof HTMLElement) {
        this.#refreshCarousels(panel);
      }
    });

    this.#syncArrows();
  }

  get #activePanel() {
    return this.#items[this.#activeIndex]?.querySelector('[data-tab-panel]') || null;
  }

  get #activeSlideshow() {
    const panel = this.#activePanel;
    return panel instanceof HTMLElement ? panel.querySelector('slideshow-component') : null;
  }

  #syncArrows() {
    const slideshow = this.#activeSlideshow;
    const slides = slideshow?.refs?.slides;
    const slideCount = slides?.length || 0;
    const canScroll = Boolean(slideshow) && !slideshow.disabled && slideCount > 1;
    const atStart = !canScroll || slideshow.current === 0;
    const atEnd = !canScroll || slideshow.nextIndex >= slideCount;

    if (this.#prevButton) this.#prevButton.disabled = atStart;
    if (this.#nextButton) this.#nextButton.disabled = atEnd;
  }

  /**
   * Recalculate carousel measurements after a hidden panel becomes visible.
   * @param {HTMLElement} panel
   */
  #refreshCarousels(panel) {
    const slideshows = panel.querySelectorAll('slideshow-component');
    if (slideshows.length === 0) {
      this.#syncArrows();
      return;
    }

    requestAnimationFrame(() => {
      slideshows.forEach((slideshow) => {
        if (typeof slideshow.select === 'function') {
          const current = Number(slideshow.current) || 0;
          slideshow.select(current, undefined, { animate: false });
        }
      });
      this.#syncArrows();
    });
  }
}

if (!customElements.get('tab-switch-products')) {
  customElements.define('tab-switch-products', TabSwitchProducts);
}
